import api from './api';

export interface TrailStatus {
  uiState: 'waiting' | 'on' | 'snoozed' | 'disabled';
  autoTrailEnabled: boolean;
  autoTrailEligibleAt?: string;
  autoTrailMutedUntil?: string;
  autoTrailManuallyMuted: boolean;
  autoTrailActivatedAt?: string;
  originIntervalMinutes: number;
  originIntervalSource: string;
  trailingMode: 'off' | 'normal' | 'spike';
  currentTrailingSl?: number;
  spikeModeActive: boolean;
  slBreachActive: boolean;
  slBreachStartedAt?: string;
  highestPriceSeen?: number;
  lowestPriceSeen?: number;
  mfePoints?: number;
  mfeRMultiple?: number;
  lastTrailComputedAt?: string;
  // R-multiple gate visibility (sole gate; time-elapsed kept only for tooltip context)
  trailSlActivationR?: number;
  rEligible?: boolean;
}

export interface TrailSettings {
  id?: number;
  userId?: number;
  autoTrailActivationMultiplier: number;
  trailDefaultIntervalForSyncedTrades: number;
  slBreachHoldMinutes: number;
  spikeModeEnabled: boolean;
  spikeTriggerAtrMultiple: number;
  spikeTriggerWindowMinutes: number;
  spikeCandleRangeMultiplier: number;
  spikeBreachHoldSeconds: number;
  profitLockAt2RPercent: number;
  profitLockAt3RPercent: number;
  profitLockAt4RPercent: number;
  trailUseCurrentCandle: boolean;
  trailBreachBufferType: string;
  trailBreachBufferValue: number;
  /** R115 Stage 4 — Auto-Target risk multiple (Target = entry ± N × risk). */
  targetRiskMultiple: number;
  /** R133 — TradeDesk FastLane (per-second hot-trade watcher). */
  fastLaneEnabled?: boolean;
  fastLaneIntervalSeconds?: number;
  /** Spike mode only starts once the trade is this many R in profit (default 5). */
  spikeActivationR?: number;
  /** Stop confirmation default: 'HOLD' (price beyond for the hold window) | 'CANDLE_CLOSE'. */
  slConfirmMode?: 'HOLD' | 'CANDLE_CLOSE';
  /** Candle timeframe (minutes) judged in CANDLE_CLOSE; null = each trade's own timeframe. */
  slCloseTfMinutes?: number | null;
  /** Emergency stop distance beyond the stop, in R (CANDLE_CLOSE; also the backup-stop level). */
  slEmergencyR?: number;
  /** Reduce-only backup stop order resting at Delta at the emergency level. Default OFF. */
  brokerBackupStopEnabled?: boolean;
}

const trailService = {
  getStatus: async (tradeId: number): Promise<TrailStatus> => {
    const res = await api.get(`/trades/${tradeId}/trail/status`);
    return (res.data as any).data;
  },

  snooze: async (tradeId: number, minutes: number): Promise<TrailStatus> => {
    const res = await api.post(`/trades/${tradeId}/trail/snooze`, { minutes });
    return (res.data as any).data;
  },

  resume: async (tradeId: number): Promise<TrailStatus> => {
    const res = await api.post(`/trades/${tradeId}/trail/resume`);
    return (res.data as any).data;
  },

  /** Permanently turn auto-trail OFF (until resumed). Clears the trailing SL so
   *  the user controls the operative SL manually (drag on chart / edit here). */
  disable: async (tradeId: number): Promise<TrailStatus> => {
    const res = await api.post(`/trades/${tradeId}/trail/disable`);
    return (res.data as any).data;
  },

  getSettings: async (): Promise<TrailSettings> => {
    const res = await api.get('/trades/trail-settings');
    return (res.data as any).data;
  },

  saveSettings: async (settings: Partial<TrailSettings>): Promise<TrailSettings> => {
    const res = await api.put('/trades/trail-settings', settings);
    return (res.data as any).data;
  },
};

export default trailService;
